import { useCallback, useEffect, useMemo, useState } from 'react'

import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'

import { IResourceColumn } from '../types'

interface ColumnConfigData {
  dataKey: string
  isFixed: boolean
  isHidden: boolean
}

interface UseColumnConfigParams {
  initialColumns: IResourceColumn[]
  projectId?: string
  userId?: string
  storageKey: LocalStorageKey
  /** dataKeys visible by default; every other column starts hidden. Omit to show all. */
  defaultVisibleDataKeys?: string[]
  /** dataKeys frozen by default. Omit to freeze the leading column only. */
  defaultFrozenDataKeys?: string[]
}

export default function useColumnConfig({
  initialColumns,
  projectId,
  userId,
  storageKey,
  defaultVisibleDataKeys,
  defaultFrozenDataKeys,
}: UseColumnConfigParams) {
  const [configuredColumns, setConfiguredColumns] = useState<IResourceColumn[]>(
    [],
  )
  // Absent an allow-list every column shows, which is what tables without
  // configured defaults have always done.
  const isHiddenByDefault = useCallback(
    (dataKey: string) =>
      !!defaultVisibleDataKeys && !defaultVisibleDataKeys.includes(dataKey),
    [defaultVisibleDataKeys],
  )

  const defaultColumns = useMemo(
    () =>
      initialColumns.map((col, idx) => {
        // The selection checkbox is structural — never hidden, never unfrozen,
        // and never part of a visibility allow-list.
        if (col.dataKey === 'checkbox') {
          return { ...col, isHidden: false, isFixed: true }
        }

        return {
          ...col,
          isHidden: isHiddenByDefault(col.dataKey),
          isFixed: defaultFrozenDataKeys
            ? defaultFrozenDataKeys.includes(col.dataKey)
            : idx === 0,
        }
      }),
    [defaultFrozenDataKeys, initialColumns, isHiddenByDefault],
  )

  const loadStoredConfig = useCallback((): ColumnConfigData[] | null => {
    if (!userId || !projectId) return null

    try {
      const userPrefs = JSON.parse(localStorage.getItem(userId) || '{}')
      const columnConfig = userPrefs[storageKey] || {}
      return columnConfig[projectId] || null
    } catch {
      return null
    }
  }, [projectId, storageKey, userId])

  const saveConfig = useCallback(
    (columns: IResourceColumn[]) => {
      if (!userId || !projectId) return

      try {
        const userPrefs = JSON.parse(localStorage.getItem(userId) || '{}')
        const columnConfig = userPrefs[storageKey] || {}

        columnConfig[projectId] = columns
          .filter(col => col.dataKey !== 'checkbox')
          .map(col => ({
            dataKey: col.dataKey,
            isFixed: !!col.isFixed,
            isHidden: !!col.isHidden,
          }))

        userPrefs[storageKey] = columnConfig
        localStorage.setItem(userId, JSON.stringify(userPrefs))
      } catch {
        return
      }
    },
    [projectId, storageKey, userId],
  )

  const clearStoredConfig = useCallback(() => {
    if (!userId || !projectId) return

    try {
      const userPrefs = JSON.parse(localStorage.getItem(userId) || '{}')
      const columnConfig = userPrefs[storageKey]
      if (!columnConfig || !(projectId in columnConfig)) return

      delete columnConfig[projectId]
      userPrefs[storageKey] = columnConfig
      localStorage.setItem(userId, JSON.stringify(userPrefs))
    } catch {
      return
    }
  }, [projectId, storageKey, userId])

  useEffect(() => {
    if (initialColumns.length === 0) return

    const storedConfig = loadStoredConfig()

    if (!storedConfig) {
      setConfiguredColumns(defaultColumns)
      return
    }

    const mergedColumns: IResourceColumn[] = []
    const processedKeys = new Set<string>()
    const checkboxColumn = initialColumns.find(
      col => col.dataKey === 'checkbox',
    )

    if (checkboxColumn) {
      mergedColumns.push({
        ...checkboxColumn,
        isFixed: true,
        isHidden: false,
      })
      processedKeys.add('checkbox')
    }

    storedConfig.forEach(cfg => {
      if (cfg.dataKey === 'checkbox') return

      const matchingCol = initialColumns.find(
        col => col.dataKey === cfg.dataKey,
      )
      if (matchingCol) {
        mergedColumns.push({
          ...matchingCol,
          isFixed: cfg.isFixed,
          isHidden: cfg.isHidden,
        })
        processedKeys.add(cfg.dataKey)
      }
    })

    // Columns the stored config predates — a property added to the project since
    // the user last saved — land at the end and still honor the visibility
    // allow-list. Freezing stays untouched: appended columns aren't leading ones.
    initialColumns.forEach((col, idx) => {
      if (!processedKeys.has(col.dataKey)) {
        mergedColumns.push({
          ...col,
          isFixed: idx === 0 && mergedColumns.length === 0,
          isHidden: isHiddenByDefault(col.dataKey),
        })
      }
    })

    setConfiguredColumns(mergedColumns)
  }, [defaultColumns, initialColumns, isHiddenByDefault, loadStoredConfig])

  const excludedFromSortKeys = useMemo(() => ['checkbox'], [])

  const resetColumnsConfiguration = useCallback(() => {
    setConfiguredColumns(defaultColumns)
    clearStoredConfig()
  }, [defaultColumns, clearStoredConfig])

  const updateColumnsConfiguration = useCallback(
    (
      collection: Pick<IResourceColumn, 'dataKey' | 'isHidden' | 'isFixed'>[],
    ) => {
      setConfiguredColumns(current => {
        const baseMap = new Map(current.map(c => [c.dataKey, c]))
        const next: IResourceColumn[] = []
        const checkboxBase = baseMap.get('checkbox')

        if (checkboxBase) {
          next.push({
            ...checkboxBase,
            isFixed: true,
            isHidden: false,
          })
        }

        collection.forEach(colCfg => {
          if (colCfg.dataKey === 'checkbox') return

          const base = baseMap.get(colCfg.dataKey)
          if (base) {
            next.push({
              ...base,
              isHidden: !!colCfg.isHidden,
              isFixed: !!colCfg.isFixed,
            })
          }
        })

        current.forEach(base => {
          if (!next.find(c => c.dataKey === base.dataKey)) next.push(base)
        })
        saveConfig(next)
        return next
      })
    },
    [saveConfig],
  )

  const fixedColumnsCount = useMemo(() => {
    return configuredColumns.filter(c => c.isFixed).length
  }, [configuredColumns])

  return {
    configuredColumns,
    defaultColumns,
    excludedFromSortKeys,
    resetColumnsConfiguration,
    updateColumnsConfiguration,
    fixedColumnsCount,
  }
}
